import { isEmpty } from 'lodash'

import constants from 'app/constants'
import {
  announcementDataStructure,
  announcementDataTransformer,
  interactionDataTransformer,
  ticketDataTransformer
} from 'app/transformer'
import { sentryLogger } from 'sentry'

import { apiEndpoints } from './common'

import { makeRequestUrl } from '../app/helpers'
import { get, post, patch, put } from '../request'

const getTargetAudienceType = (userType) => {
  if (userType === constants.STUDENT) return 'students'
  else if (userType === constants.TEACHER) return 'teachers'
  else if (userType === constants.PARENT) return 'parents'
}

const bulkMessagesRequestStructure = (data) => ({
  params: data.params.join(','),
  template: data.template,
  ...(!isEmpty(data.others) ? { others: data.others } : null),
  filters: {
    [getTargetAudienceType(data.userType)]: {
      ...(data.filters || {})
    }
  },
  ...(data.excludedUsers
    ? {
        exclude_users: {
          [getTargetAudienceType(data.userType)]: data.excludedUsers
        }
      }
    : null),
  ...(data.includedUsers
    ? {
        include_users: {
          [getTargetAudienceType(data.userType)]: data.includedUsers
        }
      }
    : null)
})

const templateMessageRequestStructure = (data) => ({
  template_name: data?.template_name,
  params: data?.params
})

const fetchWhatsAppTemplates = async ({ url, queries } = {}) => {
  const requestUrl = !isEmpty(url)
    ? url
    : makeRequestUrl(`${apiEndpoints.thirdparty}/whatsapp/templates`, queries)

  try {
    const response = await get(requestUrl)

    return response.data.items
      .filter((item) => item.status === 'APPROVED')
      .map((item) => ({
        value: item.id,
        name: item.name,
        components: item.components
      }))
  } catch (error) {
    throw error
  }
}

const fetchBulkMessages = async ({ url, queries } = {}) => {
  const requestUrl = !isEmpty(url)
    ? url
    : makeRequestUrl(
        `${apiEndpoints.thirdparty}/whatsapp/messages/bulk`,
        queries
      )

  try {
    const response = await get(requestUrl)
    return response.data
  } catch (error) {
    throw error
  }
}

const getBulkMessage = async (id) => {
  const requestUrl = `${apiEndpoints.thirdparty}/whatsapp/messages/bulk/${id}`

  try {
    const response = await get(requestUrl)

    return response.data
  } catch (error) {
    throw error
  }
}

const sendBulkMessages = async (data) => {
  try {
    const response = await post(
      `${apiEndpoints.thirdparty}/whatsapp/messages/bulk`,
      bulkMessagesRequestStructure(data)
    )

    return response.data
  } catch (error) {
    throw error
  }
}

const sendTemplateMessage = async (data) => {
  try {
    const response = await post(
      `${apiEndpoints.thirdparty}/whatsapp/templates/send`,
      templateMessageRequestStructure(data)
    )

    return response.data
  } catch (error) {
    throw error
  }
}

const createWhatsAppTemplate = async (data) => {
  try {
    const response = await post(`${apiEndpoints.thirdparty}/whatsapp`, {
      template_name: data.template_name,
      destination_number: data.destination_number,
      message_string: data.message_string,
      parameters: data.parameters
    })

    return response.data
  } catch (error) {
    throw error
  }
}

const fetchReportCard = async ({ url, queries } = {}) => {
  const requestUrl = !isEmpty(url)
    ? url
    : makeRequestUrl(
        `${apiEndpoints.ohClasses}/report_card/dashboard/`,
        queries
      )

  try {
    const response = await get(requestUrl)

    return {
      ...response.data,
      results: response.data.results.map((item) => ({
        ...item,
        created: item.created
      }))
    }
  } catch (error) {
    throw error
  }
}

const createReportCard = async (data) => {
  try {
    // Create an object of formData
    const formData = new FormData()
    // Update the formData object
    formData.append('file', data, data.name)
    const response = await post(
      `${apiEndpoints.ohClasses}/report_card/generate/`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    )

    return response.data
  } catch (error) {
    throw error
  }
}

const fetchInteractionHistory = async ({ url, queries } = {}) => {
  const requestUrl = !isEmpty(url)
    ? url
    : makeRequestUrl(`${apiEndpoints.interactions}`, queries)
  try {
    const response = await get(requestUrl)
    return interactionDataTransformer(response?.data)
  } catch (error) {
    sentryLogger(error, 'error in fetching interaction history')
    throw error
  }
}
const updateInteractionHistory = async (data) => {
  const {
    interaction_date,
    connected_status,
    parent,
    interaction_mode = null,
    interaction_summary = null,
    interaction_topics = [],
    follow_up_date = null
  } = data

  const payload = {
    interaction_date,
    connected_status,
    parent,
    interaction_mode: !isEmpty(interaction_mode) ? interaction_mode : null,
    interaction_summary,
    interaction_topics: !isEmpty(interaction_topics) ? interaction_topics : null,
    // The FollowUpWidget uses '' (unanswered) and 'yes' (date pending) as
    // sentinels — only a real date is ever persisted.
    follow_up_date: /^\d{4}-\d{2}-\d{2}$/.test(follow_up_date || '')
      ? follow_up_date
      : null
  }
  try {
    const response = await post(`${apiEndpoints.interactions}/`, payload)
    return response.data
  } catch (error) {
    throw error
  }
}

// Edit an already-logged interaction (the "edit" action on the Alfred parent
// dashboard's interactions drawer). PATCH — `parent` and `created_by` are not
// sent and are immutable server-side, so an edit can never re-point an
// interaction at another family or rewrite who logged it.
const updateInteraction = async (uuid, data) => {
  const {
    interaction_date,
    connected_status,
    interaction_mode = null,
    interaction_topics = [],
    follow_up_date = null
  } = data

  const payload = {
    interaction_date,
    connected_status,
    interaction_mode: !isEmpty(interaction_mode) ? interaction_mode : null,
    // Only the topics the form actually rendered are replaced. A non-Connected
    // interaction has no summary section at all, so send an empty list rather
    // than null — the backend replaces the summaries whenever the key is
    // present, and `null` would read as "no topics" just the same.
    interaction_topics: interaction_topics || [],
    // The FollowUpWidget uses '' (unanswered) and 'yes' (date pending) as
    // sentinels — only a real date is ever persisted.
    follow_up_date: /^\d{4}-\d{2}-\d{2}$/.test(follow_up_date || '')
      ? follow_up_date
      : null
  }
  try {
    const response = await patch(
      `${apiEndpoints.interactions}/${uuid}/`,
      payload
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in updating interaction')
    throw error
  }
}

// Voice-to-form: send a recorded interaction note (WAV) to the backend, which
// transcribes it and — for a templated topic — extracts the template's fields.
// The audio is used only to fill the form and is not stored. `fields` is the
// topic's template field defs ([{key,label,type,options}]) or [] for free text.
const transcribeInteractionAudio = async ({ audio, topic = '', fields = [] }) => {
  const formData = new FormData()
  formData.append('audio', audio, 'interaction.wav')
  if (topic) formData.append('topic', topic)
  if (fields && fields.length) formData.append('fields', JSON.stringify(fields))
  try {
    const response = await post(
      `${apiEndpoints.interactions}/voice-fill/`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in transcribing interaction audio')
    throw error
  }
}

const fetchInteractionTopicOptions = async () => {
  const requestUrl = makeRequestUrl(`${apiEndpoints.configApp}`, {
    key: 'CRM_INTERACTION_TOPICS'
  })
  try {
    const response = await get(requestUrl)
    return response.data
  } catch (error) {
    console.log(error, 'error in fetching deduction reasons')
    throw error
  }
}

// Every topic ever stored (current list first, then the retired names), for
// filters and for showing an old interaction's topic (user 2026-09-24). Falls
// back to the current list if the config key doesn't exist yet.
const fetchAllInteractionTopicOptions = async () => {
  try {
    const response = await get(
      makeRequestUrl(`${apiEndpoints.configApp}`, { key: 'CRM_INTERACTION_TOPICS_ALL' })
    )
    if (response.data?.results?.[0]?.value?.length) return response.data
  } catch (error) {
    console.log(error, 'error in fetching all interaction topics')
  }
  return fetchInteractionTopicOptions()
}

const fetchInteractionConnectedStatusOptions = async () => {
  const requestUrl = makeRequestUrl(`${apiEndpoints.configApp}`, {
    key: 'CRM_INTERACTION_CONNECTED_STATUSES'
  })
  try {
    const response = await get(requestUrl)
    return response.data
  } catch (error) {
    console.log(error, 'error in fetching connected status options')
    throw error
  }
}

const fetchInteractionModeOptions = async () => {
  const requestUrl = makeRequestUrl(`${apiEndpoints.configApp}`, {
    key: 'CRM_INTERACTION_MODES'
  })
  try {
    const response = await get(requestUrl)
    return response.data
  } catch (error) {
    console.log(error, 'error in fetching interaction mode options')
    throw error
  }
}

const fetchTickets = async ({ url, queries } = {}) => {
  const requestUrl = !isEmpty(url)
    ? url
    : makeRequestUrl(`${apiEndpoints.tickets}`, queries)
  try {
    const response = await get(requestUrl)

    return ticketDataTransformer(response?.data)
  } catch (error) {
    sentryLogger(error, 'error in fetching tickets')
    throw error
  }
}

const updateTicket = async (data) => {
  const {
    user,
    status,
    type,
    mode,
    description,
    title,
    uuid,
    resolution_summary,
    follow_up_date,
    resolution_conclusion,
    conversion_intent,
    interested_programs,
    tentative_joining_date,
    intent_reason
  } = data

  const isLowIntent = conversion_intent === 'Low'
  const normalizedInterestedPrograms = isLowIntent
    ? []
    : interested_programs || []
  const normalizedTentativeJoiningDate = isLowIntent
    ? null
    : tentative_joining_date || null

  let payload = mode
    ? {
        user,
        status,
        mode,
        type,
        description,
        title,
        follow_up_date: follow_up_date || null,
        resolution_conclusion: resolution_conclusion || null,
        conversion_intent: conversion_intent || null,
        interested_programs: normalizedInterestedPrograms,
        tentative_joining_date: normalizedTentativeJoiningDate,
        intent_reason: intent_reason || null
      }
    : {}
  payload = resolution_summary
    ? {
        ...payload,
        user,
        status,
        resolution_summary,
        follow_up_date: follow_up_date || null,
        resolution_conclusion: resolution_conclusion || null,
        conversion_intent: conversion_intent || null,
        interested_programs: normalizedInterestedPrograms,
        tentative_joining_date: normalizedTentativeJoiningDate,
        intent_reason: intent_reason || null
      }
    : {
        ...payload,
        user,
        status,
        follow_up_date: follow_up_date || null,
        resolution_conclusion: resolution_conclusion || null,
        conversion_intent: conversion_intent || null,
        interested_programs: normalizedInterestedPrograms,
        tentative_joining_date: normalizedTentativeJoiningDate,
        intent_reason: intent_reason || null
      }

  try {
    const response = await patch(`${apiEndpoints.tickets}/${uuid}/`, payload)
    return response.data
  } catch (error) {
    throw error
  }
}

const createTicket = async ({
  user,
  type,
  mode,
  description,
  title,
  status,
  follow_up_date,
  conversion_intent,
  interested_programs,
  tentative_joining_date,
  intent_reason
}) => {
  const isLowIntent = conversion_intent === 'Low'
  const normalizedInterestedPrograms = isLowIntent
    ? []
    : interested_programs || []
  const normalizedTentativeJoiningDate = isLowIntent
    ? null
    : tentative_joining_date || null

  const payload = {
    user,
    type,
    mode,
    title,
    description,
    status,
    follow_up_date: follow_up_date || null,
    conversion_intent: conversion_intent || null,
    interested_programs: normalizedInterestedPrograms,
    tentative_joining_date: normalizedTentativeJoiningDate,
    intent_reason: intent_reason || null
  }
  try {
    const response = await post(`${apiEndpoints.tickets}/`, payload)
    return response.data
  } catch (error) {
    throw error
  }
}

const fetchTicketStatusOptions = async () => {
  const requestUrl = makeRequestUrl(`${apiEndpoints.configApp}`, {
    key: 'CRM_TICKET_STATUSES'
  })
  try {
    const response = await get(requestUrl)
    return response.data
  } catch (error) {
    console.log(error, 'error in fetching deduction reasons')
    throw error
  }
}

const fetchTicketTypeOptions = async () => {
  const requestUrl = makeRequestUrl(`${apiEndpoints.configApp}`, {
    key: 'CRM_TICKET_TYPES'
  })

  try {
    const response = await get(requestUrl)

    return response.data
  } catch (error) {
    console.log(error, 'error in fetching deduction reasons')
    throw error
  }
}

const fetchInterestedProgramsOptions = async () => {
  const requestUrl = makeRequestUrl(`${apiEndpoints.configApp}`, {
    key: 'INTERESTED_PROGRAMS_CONFIG'
  })
  try {
    const response = await get(requestUrl)

    return response.data
  } catch (error) {
    console.log(error, 'error in fetching deduction reasons')
    throw error
  }
}

const fetchResolutionConclusionsOptions = async () => {
  const requestUrl = makeRequestUrl(`${apiEndpoints.configApp}`, {
    key: 'CRM_TICKET_RESOLUTION_CONCLUSIONS'
  })

  try {
    const response = await get(requestUrl)

    return response.data
  } catch (error) {
    console.log(error, 'error in fetching resolution conclusions')
    throw error
  }
}

const fetchAnnouncements = async ({ url, queries } = {}) => {
  const requestUrl = !isEmpty(url)
    ? url
    : makeRequestUrl(`${apiEndpoints.announcements}`, queries)
  try {
    const response = await get(requestUrl)

    return announcementDataTransformer(response?.data)
  } catch (error) {
    sentryLogger(error, 'error in fetching tickets')
    throw error
  }
}

const updateAnnouncement = async (uuid, data) => {
  const payload = announcementDataStructure(data)
  console.log('update payload', payload)

  try {
    const response = await patch(
      `${apiEndpoints.announcements}/${uuid}/`,
      payload
    )
    return response.data
  } catch (error) {
    throw error
  }
}

const createAnnouncement = async (data) => {
  const payload = announcementDataStructure(data)
  console.log('payload', payload)

  try {
    const response = await post(`${apiEndpoints.announcements}`, payload)
    return response.data
  } catch (error) {
    throw error
  }
}

const updateAnnouncementStatus = async (uuid, status) => {
  try {
    const response = await patch(`${apiEndpoints.announcements}/${uuid}/`, {
      status
    })
    return response.data
  } catch (error) {
    throw error
  }
}

const saveAnnouncementOrder = async (orderData) => {
  try {
    const response = await put(
      `${apiEndpoints.announcements}/order/`,
      orderData
    )
    return response.data
  } catch (error) {
    throw error
  }
}

export {
  fetchWhatsAppTemplates,
  fetchBulkMessages,
  createWhatsAppTemplate,
  fetchReportCard,
  createReportCard,
  sendBulkMessages,
  getBulkMessage,
  sendTemplateMessage,
  fetchInteractionHistory,
  updateInteractionHistory,
  updateInteraction,
  fetchInteractionTopicOptions,
  fetchAllInteractionTopicOptions,
  fetchInteractionConnectedStatusOptions,
  fetchInteractionModeOptions,
  transcribeInteractionAudio,
  fetchTickets,
  updateTicket,
  createTicket,
  fetchTicketStatusOptions,
  fetchTicketTypeOptions,
  fetchResolutionConclusionsOptions,
  fetchAnnouncements,
  updateAnnouncement,
  createAnnouncement,
  updateAnnouncementStatus,
  saveAnnouncementOrder,
  fetchInterestedProgramsOptions
}
